import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { auth, db } from "../../../firebase.config";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { handleAuthError, handleAuthFlow } from "../auth-utils";
import { Eye, EyeOff } from 'lucide-react';
import useCart from "../../../hooks/useCart";
import { initialPlan } from "../../../constants/plans";

const signupSchema = z
  .object({
    email: z.string().email("Invalid email format"),
    firstName: z.string().min(2, "First name must be at least 2 characters"),
    lastName: z.string(),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

// Custom button styles for hover animation (Copied from Login)
const buttonHoverProps = (isSubmitting) => ({
  onMouseEnter: (e) => {
    if (!isSubmitting) {
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 20px 50px rgba(249, 115, 22, 0.6)';
      e.currentTarget.style.opacity = '0.96';
    }
  },
  onMouseLeave: (e) => {
    if (!isSubmitting) {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 16px 40px rgba(249, 115, 22, 0.55)';
      e.currentTarget.style.opacity = '1';
    }
  }
});

export default function Signup() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
  });

  const { addToCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const setRememberEmail = (mail) => {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', mail);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
  };

  const onSubmit = async (data) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const user = userCredential.user;
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);

      // Recovery flow: Check for orphaned order by email (fallback for edge cases)
      let hasPaid = false;
      const ordersRef = collection(db, "orders");
      const q = query(ordersRef, where("guestEmail", "==", data.email), where("userId", "==", null));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const orderDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, "orders", orderDoc.id), {
          userId: user.uid,
        });
        hasPaid = true;
        toast.success("Previous payment recovered and linked!");
      }

      await setDoc(doc(db, "users", user.uid), {
        userId: user.uid,
        email: user.email,
        role: "user",
        accept_terms: false,
        firstName: data.firstName,
        lastName: data.lastName,
        subscription_status: hasPaid,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      if (hasPaid) {
        await sendEmailVerification(user, {
          url: `${window.location.origin}/disclaimer`,
        });
        navigate("/verify-email");
      } else {
        toast.success("Account created successfully!");
        const userData = docSnap.exists() ? docSnap.data() : "";
        await handleAuthFlow(user, userData, navigate, data.email, setRememberEmail, true, addToCart, initialPlan);
      }
    } catch (err) {
      handleAuthError(err, true);
    }
  };

  const handleGoogleSignup = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      let firstName = '';
      let lastName = '';

      if (user.displayName) {
        const nameParts = user.displayName.split(' ');
        firstName = nameParts[0]; // First name
        lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''; // Last name (if any)
      }

      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email,
          accept_terms: false,
          firstName: firstName,
          lastName: lastName,
          role: "user",
          subscription_status: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }

      toast.success("Signed up with Google!");
      
      // Default to initial plan and go to checkout
      addToCart(initialPlan);
      // navigation is handled inside addToCart but we can force it if needed, 
      // check addToCart implementation: it does navigate("/checkout")
      
    } catch (error) {
      console.error("Google sign-in error:", error.message);
      toast.error("Google sign-in failed. Please try again.");
    }
  };

  const handleFacebookLogin = async () => {
    const provider = new FacebookAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if the user exists in Firestore
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);

      // If the user does not exist, it's a sign-up
      if (!docSnap.exists()) {
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email,
          accept_terms: false,
          role: "user",
          subscription_status: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        toast.success("Signed up with Facebook!");
      } else {
        toast.success("Logged in with Facebook!");
      }

      navigate("/dashboard");
    } catch (error) {
      console.error("Facebook login error:", error);
      toast.error("Facebook login failed. Try again.");
    }
  };

  // Common input styles object (Derived from Login component styles)
  const inputStyle = {
    background: 'rgba(15, 23, 42, 0.9)',
    borderColor: 'rgba(148, 163, 184, 0.3)',
    color: '#e5e7eb',
    fontSize: '0.95rem',
  };

  return (
    <div className="min-h-screen text-gray-50" style={{
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
      background: 'radial-gradient(circle at top, #111827 0, #020617 55%)',
      WebkitFontSmoothing: 'antialiased',
      lineHeight: 1.7
    }}>
      <header className="pt-8 pb-6">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-white">Ascendra</h1>
        </div>
      </header>

      <section className="py-8 px-4">
        <div className="max-w-[480px] mx-auto">
          <div className="border shadow-2xl" style={{
            padding: '32px 24px',
            borderRadius: '18px',
            background: 'rgba(15, 23, 42, 0.7)',
            borderColor: 'rgba(148, 163, 184, 0.24)',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.7)'
          }}>
            <h2 style={{
              fontSize: '1.35rem',
              color: '#9ca3af',
              justifyContent: 'center',
              display: 'flex',
              fontWeight: '600',
              marginBottom: '2rem'
            }}>
              Create your account
            </h2>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {/* Email */}
              <div>
                <label htmlFor="email" className="sr-only">Email Address</label>
                <input
                  id="email"
                  type="text"
                  {...register("email")}
                  placeholder="Email Address"
                  className="w-full px-4 py-3 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                  style={{ ...inputStyle, borderColor: errors.email ? '#ef4444' : inputStyle.borderColor }}
                />
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1.5">{errors.email.message}</p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="sr-only">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  {...register("firstName")}
                  placeholder="First Name"
                  className="w-full px-4 py-3 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                  style={{ ...inputStyle, borderColor: errors.firstName ? '#ef4444' : inputStyle.borderColor }}
                />
                {errors.firstName && (
                  <p className="text-red-400 text-sm mt-1.5">{errors.firstName.message}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="sr-only">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  {...register("lastName")}
                  placeholder="Last Name"
                  className="w-full px-4 py-3 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                  style={{ ...inputStyle, borderColor: errors.lastName ? '#ef4444' : inputStyle.borderColor }}
                />
                {errors.lastName && (
                  <p className="text-red-400 text-sm mt-1.5">{errors.lastName.message}</p>
                )}
              </div>

              {/* Password */}
              <div className="flex flex-col space-y-5">
                <div className="relative w-full">
                  <label htmlFor="password" className="sr-only">Password</label>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    placeholder="Password"
                    className="w-full px-4 py-3 pr-12 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    style={{ ...inputStyle, borderColor: errors.password ? '#ef4444' : inputStyle.borderColor }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                  {errors.password && (
                    <p className="text-red-400 text-sm mt-1.5 block">{errors.password.message}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="relative w-full">
                  <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirmPassword")}
                    placeholder="Confirm Password"
                    className="w-full px-4 py-3 pr-12 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    style={{ ...inputStyle, borderColor: errors.confirmPassword ? '#ef4444' : inputStyle.borderColor }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                  {errors.confirmPassword && (
                    <p className="text-red-400 text-sm mt-1.5 block">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center rounded-full border-0 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  padding: '12px 22px',
                  background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #f97316, #fb923c)',
                  color: '#0b1120',
                  fontWeight: 600,
                  fontSize: '0.98rem',
                  boxShadow: isSubmitting ? 'none' : '0 16px 40px rgba(249, 115, 22, 0.55)',
                  transform: 'translateY(0)'
                }}
                {...buttonHoverProps(isSubmitting)}
              >
                {isSubmitting ? "Creating..." : "Create Account"}
              </button>
            </form>

            {/* Social login separator */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'rgba(148, 163, 184, 0.3)' }}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 text-gray-400" style={{ background: 'rgba(15, 23, 42, 0.7)' }}>
                  or continue with
                </span>
              </div>
            </div>

            {/* Social Login Buttons */}
            <div className="flex justify-center space-x-4">
              {/* Google Login Button */}
              <button
                onClick={handleGoogleSignup}
                className="w-12 h-12 rounded-full flex items-center justify-center border transition-all hover:scale-105"
                style={{
                  background: 'rgba(15, 23, 42, 0.9)',
                  borderColor: 'rgba(148, 163, 184, 0.3)'
                }}
                title="Sign up with Google"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </button>

              {/* Facebook Login Button */}
              {/* <button
                onClick={handleFacebookLogin}
                className="w-12 h-12 rounded-full flex items-center justify-center border transition-all hover:scale-105"
                style={{
                  background: 'rgba(15, 23, 42, 0.9)',
                  borderColor: 'rgba(148, 163, 184, 0.3)'
                }}
                title="Sign up with Facebook"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#1877F2" d="M22.5 12.001c0-5.834-4.666-10.5-10.5-10.5S1.5 6.167 1.5 12.001c0 5.234 3.791 9.584 8.718 10.375v-7.348H7.425V12.001h2.793v-2.1c0-2.768 1.62-4.298 4.187-4.298 1.201 0 2.228.214 2.531.309v2.86h-1.688c-1.325 0-1.577.629-1.577 1.55v2.071h3.143l-.504 3.125h-2.639v7.348c4.927-.79 8.718-5.141 8.718-10.375z" />
                </svg>
              </button> */}
            </div>

            {/* Remember me (Re-integrated with state logic) */}
            <div className="flex items-center justify-center mt-5">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className="w-4 h-4 rounded border-gray-600 bg-slate-800 text-orange-500 focus:ring-2 focus:ring-orange-500 focus:ring-offset-0"
              />
              <label htmlFor="remember-me" className="ml-2 text-sm text-gray-300">
                Remember Me
              </label>
            </div>

            <div className="text-center mt-5">
              <p className="text-sm text-gray-300">
                Already have an account?{" "}
                <Link to="/login" className="text-orange-400 font-medium hover:text-orange-300 transition-colors">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center mt-6 text-sm italic" style={{ color: '#9ca3af' }}>
            "Start your journey to freedom."
          </p>
        </div>
      </section>
    </div>
  );
}